import React from "react";
import { ShieldCheck, Sparkles } from "lucide-react";
import PageMeta from "../components/PageMeta";
import CTABanner from "../components/CTABanner";

const SECTIONS = [
  {
    title: "1. Information we collect",
    body: [
      "When you fill in a form on our website, book a consultation or chat with a specialist, we collect the details you voluntarily provide — typically your name, email address, phone number, country, and a short description of the Wikipedia project you're enquiring about.",
      "We also automatically collect standard technical information such as your IP address, browser user-agent, timezone, approximate city-level location, referring page and anonymised usage events. This is used purely to keep the site secure, debug issues and understand which pages convert.",
    ],
  },
  {
    title: "2. How we use your information",
    body: [
      "Your details are used to respond to your enquiry, prepare a confidential notability assessment, send you relevant proposals, and — if you become a client — to deliver and maintain your Wikipedia engagement.",
      "We never sell or rent your personal information to any third party. We never use your data to train public AI models. We don't enrich it against data brokers.",
    ],
  },
  {
    title: "3. Legal basis (GDPR / UK GDPR)",
    body: [
      "Our legal basis for processing your information is (a) your explicit consent when you submit a form, (b) the performance of a contract when you become a client, and (c) our legitimate interest in operating a secure, fraud-resistant website.",
      "You may withdraw consent or request deletion at any time by emailing info@Primewikiservices.com.",
    ],
  },
  {
    title: "4. Data retention",
    body: [
      "Enquiry data is retained for up to 24 months from the last interaction so we can follow up with qualifying leads and honour any promised discount. Client project data is retained for the duration of the engagement plus 7 years to comply with tax and audit obligations.",
      "You can request earlier deletion at any time, and we will erase your personal information within 30 days of a verified request, unless we're required to keep a specific record by law.",
    ],
  },
  {
    title: "5. Third-party processors we use",
    body: [
      "We use a small, carefully-chosen set of processors to run our service: a CRM for lead management, a live-chat provider for real-time support, a transactional email provider, cloud hosting (EU and US regions) and an anonymised analytics tool. Each provider is bound by a signed Data Processing Agreement and is either GDPR-compliant or covered by the EU–US Data Privacy Framework.",
    ],
  },
  {
    title: "6. Cookies",
    body: [
      "We use a minimal number of strictly necessary cookies (to keep you signed in if applicable and to remember cookie preferences) and optional analytics cookies (to understand anonymised page performance). Optional cookies are never dropped unless you have explicitly consented.",
    ],
  },
  {
    title: "7. Your rights",
    body: [
      "Under GDPR, UK GDPR and the CCPA you have the right to access, correct, delete, restrict, or port your personal information, and to object to certain types of processing. You also have the right to lodge a complaint with a data protection authority at any time.",
      "To exercise any of these rights, email info@Primewikiservices.com. We'll respond within 30 days.",
    ],
  },
  {
    title: "8. Children",
    body: [
      "Our services are intended for adults and for organisations. We do not knowingly collect personal information from anyone under the age of 16. If you believe we have inadvertently done so, please contact us so we can delete the data.",
    ],
  },
  {
    title: "9. Updates to this policy",
    body: [
      "We may update this policy from time to time. The 'last updated' date at the top of this page reflects the most recent change. Material changes will be announced via a banner on our homepage for 30 days.",
    ],
  },
  {
    title: "10. Contact",
    body: [
      "Questions about this policy, or a specific request regarding your data, can be sent to info@Primewikiservices.com or by post to: Prime Wiki Services, 360 S Market St, San Jose, CA 95113, USA.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="overflow-hidden" data-testid="privacy-page">
      <PageMeta
        title="Privacy Policy — Prime Wiki Services"
        description="How Prime Wiki Services collects, uses, stores and protects your personal information when you engage us for Wikipedia page creation, editing or monitoring services."
        keywords="privacy policy, my wiki brand privacy, data protection, gdpr, ccpa"
        noindex={false}
      />

      {/* Hero banner */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8" data-testid="privacy-hero">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Privacy Policy
          </span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
            Your information, treated with the respect it deserves.
          </h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">
            We collect the minimum we need, keep it for the shortest time we can, and never sell it — ever.
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">Last updated · 24 February 2026</p>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="privacy-body">
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <article key={s.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_40px_-25px_rgba(10,37,64,0.25)]">
              <h2 className="font-serif text-2xl font-semibold text-[#0A2540]">{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-3 text-[15px] leading-relaxed text-slate-600">{p}</p>
              ))}
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-sm text-slate-700">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" /> In plain English
          </div>
          <p className="mt-3">
            We only ask you for what we need to help you build a Wikipedia page. We keep it safe, delete it when you ask us to, and never share it with anyone who isn't directly helping us deliver your engagement.
          </p>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
